import { useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { cx } from '../charte';
import { Sheet } from './Sheet';
import { bottomTabs, unreadLabel, type NavItem, type Tab } from './nav';

function Count({ value, className }: { value: number; className?: string }) {
  if (value <= 0) return null;
  return (
    <span className={cx('grid h-4 min-w-[16px] place-items-center rounded-pill bg-danger px-1 text-[10px] font-bold leading-none text-white', className)}>
      <span aria-hidden="true">{value > 99 ? '99+' : value}</span>
      <span className="sr-only">{unreadLabel(value)}</span>
    </span>
  );
}

/** Onglets de la barre du haut (ordinateur). `wide` : trop d'onglets pour un écran de 1024 px. */
export function TopTabs({
  items,
  tab,
  onTab,
  unreadMessages,
  wide,
}: {
  items: NavItem[];
  tab: Tab;
  onTab: (t: Tab) => void;
  unreadMessages: number;
  wide: boolean;
}) {
  return (
    <nav aria-label="Navigation principale" className={cx('hidden min-w-0 items-center gap-0.5', wide ? 'xl:flex' : 'lg:flex')}>
      {items.map((item) => {
        const active = tab === item.id;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onTab(item.id)}
            aria-current={active ? 'page' : undefined}
            className={cx(
              'inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-pill px-3 text-[13.5px] font-medium transition-colors',
              active ? 'bg-primary-50 text-primary-600' : 'text-secondary hover:bg-surface-2 hover:text-body',
            )}
          >
            {item.label}
            {item.id === 'messages' && <Count value={unreadMessages} />}
          </button>
        );
      })}
    </nav>
  );
}

/** Barre fixe en bas (téléphone, tablette) : Accueil, Réserver, Messages, Paiements et « Plus ». */
export function BottomBar({
  items,
  tab,
  onTab,
  unreadMessages,
  wide,
}: {
  items: NavItem[];
  tab: Tab;
  onTab: (t: Tab) => void;
  unreadMessages: number;
  wide: boolean;
}) {
  const [moreOpen, setMoreOpen] = useState(false);
  const { main, more } = bottomTabs(items);
  const moreActive = more.some((m) => m.id === tab);
  const cell = (active: boolean) =>
    cx(
      'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1 pt-2 pb-1.5 text-[11px] font-medium transition-colors',
      active ? 'text-primary-600' : 'text-secondary hover:text-body',
    );
  const pill = (active: boolean) =>
    cx('grid h-8 w-14 place-items-center rounded-pill transition-colors', active && 'bg-primary-50');
  return (
    <>
      <nav
        aria-label="Navigation principale"
        className={cx(
          'fixed inset-x-0 bottom-0 z-40 border-t border-line bg-white/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.12)] backdrop-blur',
          wide ? 'xl:hidden' : 'lg:hidden',
        )}
      >
        <div className="mx-auto flex max-w-xl">
          {main.map(({ id, label, icon: Icon }) => {
            const active = tab === id;
            return (
              <button key={id} type="button" onClick={() => onTab(id)} aria-current={active ? 'page' : undefined} className={cell(active)}>
                <span className={pill(active)}>
                  <Icon size={20} strokeWidth={active ? 2.2 : 1.8} aria-hidden />
                </span>
                <span className="flex max-w-full items-center gap-1 truncate">
                  {label}
                  {id === 'messages' && <Count value={unreadMessages} className="absolute right-[calc(50%-24px)] top-1.5" />}
                </span>
              </button>
            );
          })}
          {more.length > 0 && (
            <button type="button" onClick={() => setMoreOpen(true)} aria-haspopup="dialog" className={cell(moreActive)}>
              <span className={pill(moreActive)}>
                <MoreHorizontal size={20} aria-hidden />
              </span>
              Plus
            </button>
          )}
        </div>
      </nav>
      {moreOpen && (
        <Sheet title="Plus" onClose={() => setMoreOpen(false)}>
          <ul className="grid gap-1">
            {more.map(({ id, label, icon: Icon }) => {
              const active = tab === id;
              return (
                <li key={id}>
                  <button
                    type="button"
                    onClick={() => {
                      onTab(id);
                      setMoreOpen(false);
                    }}
                    aria-current={active ? 'page' : undefined}
                    className={cx(
                      'flex w-full items-center gap-3 rounded-row px-3 py-3 text-left text-[15px] font-medium transition-colors',
                      active ? 'bg-primary-50 text-primary-700' : 'text-body hover:bg-surface-light',
                    )}
                  >
                    <span className={cx('grid h-9 w-9 place-items-center rounded-xl', active ? 'bg-white text-primary' : 'bg-surface-indigo text-primary')}>
                      <Icon size={18} aria-hidden />
                    </span>
                    {label}
                  </button>
                </li>
              );
            })}
          </ul>
        </Sheet>
      )}
    </>
  );
}
